declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      REACT_APP_API_URL?: string;
      REACT_APP_PAGINATION_SIZE?: string;
      REACT_APP_URL_WHATSAPP?: string;
      REACT_APP_URL_TELEGRAM?: string;
      REACT_APP_CONTACT_URL?: string;
      REACT_APP_MCP_URL?: string;
      REACT_APP_MCP_ENABLED?: string;
      REACT_APP_MCP_SAME_ORIGIN?: string;
    };
  }
}

const DEFAULT_PAGINATION_SIZE = 12;

/** Last-resort defaults for production host (overridden by runtime-config.js or CRA env). */
const HOST_COMMUNITY_DEFAULTS: Partial<
  Record<keyof NonNullable<Window['__RUNTIME_CONFIG__']>, string>
> = {
  REACT_APP_URL_WHATSAPP: 'https://chat.whatsapp.com/LWapIkVKZYD0UmApRxYQ2y',
  REACT_APP_URL_TELEGRAM: 'https://t.me/technical_opportunitties',
  REACT_APP_CONTACT_URL: 'no-reply@itjobopportunities.io',
};

const PRODUCTION_HOST_SUFFIXES = ['itjobopportunities.io'];

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function isLoopbackHost(hostname: string): boolean {
  return (
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  );
}

function isLoopbackApiUrl(url: string): boolean {
  if (!url) {
    return false;
  }
  try {
    return isLoopbackHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

function isBrowserOnLoopback(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return isLoopbackHost(window.location.hostname);
}

/**
 * Loopback API URLs baked at build time (e.g. http://localhost:8082) must not be
 * used when the UI is served from a public host — use same-origin /public/* instead.
 */
function resolveApiBaseUrl(raw: string | undefined): string {
  const trimmed = stripTrailingSlash(String(raw ?? '').trim());
  if (trimmed && isLoopbackApiUrl(trimmed) && !isBrowserOnLoopback()) {
    return '';
  }
  return trimmed;
}

/**
 * API base URL as seen from the user's browser.
 * In Docker, prefer `window.__RUNTIME_CONFIG__` (written at container start)
 * so the same image can target different backends without rebuilding.
 * Empty string → relative `/public/*` on the UI origin (nginx proxy via REACT_APP_API_URL).
 */
export function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const cfg = window.__RUNTIME_CONFIG__;
    if (Object.prototype.hasOwnProperty.call(cfg, 'REACT_APP_API_URL')) {
      return resolveApiBaseUrl(cfg.REACT_APP_API_URL);
    }
  }
  return resolveApiBaseUrl(process.env.REACT_APP_API_URL);
}

/**
 * Page size for public job listings. In Docker, read from `window.__RUNTIME_CONFIG__`
 * (written at container start) so `.env` / Dokploy values apply without rebuilding.
 */
export function getPaginationSize(): number {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const cfg = window.__RUNTIME_CONFIG__;
    if (
      Object.prototype.hasOwnProperty.call(cfg, 'REACT_APP_PAGINATION_SIZE')
    ) {
      return parsePaginationSize(cfg.REACT_APP_PAGINATION_SIZE);
    }
  }
  return parsePaginationSize(process.env.REACT_APP_PAGINATION_SIZE);
}

function getHostCommunityDefault(
  key: keyof NonNullable<Window['__RUNTIME_CONFIG__']>
): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const host = window.location.hostname.toLowerCase();
  const onProductionHost = PRODUCTION_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  );
  if (!onProductionHost) {
    return '';
  }
  return String(HOST_COMMUNITY_DEFAULTS[key] ?? '').trim();
}

function getRuntimeString(
  key: keyof NonNullable<Window['__RUNTIME_CONFIG__']>,
  fallback = ''
): string {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const cfg = window.__RUNTIME_CONFIG__;
    if (Object.prototype.hasOwnProperty.call(cfg, key)) {
      const fromRuntime = String(cfg[key] ?? '').trim();
      if (fromRuntime) {
        return fromRuntime;
      }
    }
  }
  const fromEnv = process.env[key]?.trim();
  if (fromEnv) {
    return fromEnv;
  }
  const fromHost = getHostCommunityDefault(key);
  if (fromHost) {
    return fromHost;
  }
  return fallback;
}

export function getWhatsappUrl(): string {
  return getRuntimeString('REACT_APP_URL_WHATSAPP');
}

export function getTelegramUrl(): string {
  return getRuntimeString('REACT_APP_URL_TELEGRAM');
}

export function getContactEmail(): string {
  return getRuntimeString('REACT_APP_CONTACT_URL');
}

/** When false, hide MCP UI (footer link, developers page content, home teaser). */
export function isMcpEnabled(): boolean {
  const flag = getRuntimeString('REACT_APP_MCP_ENABLED', 'true').toLowerCase();
  return flag !== 'false' && flag !== '0' && flag !== 'off';
}

/**
 * Public Streamable HTTP MCP endpoint for AI clients.
 * Prefer `REACT_APP_MCP_URL`; else `{apiBase}/mcp`; else same-origin `/mcp`
 * when Docker nginx proxies MCP (REACT_APP_MCP_SAME_ORIGIN=true).
 */
export function getMcpUrl(): string {
  if (!isMcpEnabled()) {
    return '';
  }

  const explicit = getRuntimeString('REACT_APP_MCP_URL');
  if (explicit) {
    return stripTrailingSlash(explicit);
  }

  const apiBase = getApiBaseUrl();
  if (apiBase) {
    return `${apiBase}/mcp`;
  }

  const sameOrigin = getRuntimeString(
    'REACT_APP_MCP_SAME_ORIGIN'
  ).toLowerCase();
  if (
    (sameOrigin === 'true' || sameOrigin === '1') &&
    typeof window !== 'undefined' &&
    window.location?.origin
  ) {
    return `${window.location.origin}/mcp`;
  }

  return '';
}

function parsePaginationSize(raw: string | undefined): number {
  const parsed = Number.parseInt(String(raw ?? '').trim(), 10);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return DEFAULT_PAGINATION_SIZE;
}
